/**
 * useChannelSplit — collapses the multi-channel useChannelMix output into a
 * cleaner Delivery / Dine-in / Pick-up view (the split the marketing team
 * actually thinks in). Delivery aggregates all aggregator + direct-delivery
 * channels. Pick-up is optional (usually tiny, exposed as a footnote).
 *
 * Returns each bucket with sales, orders, share %, and prior-period delta
 * so both Sales and Growth pages can render the same shape.
 */
import { useMemo } from 'react';
import { useChannelMix, type ChannelRow } from './useChannelMix';
import type { PeriodKey } from '@/lib/periods';

export interface SplitBucket {
  label: 'Delivery' | 'Dine-in' | 'Pick-up';
  sales: number;
  orders: number;
  sharePct: number;
  deltaPct: number | null;
  channels: string[];
}

const DELIVERY_CHANNELS = new Set(['Swiggy', 'Zomato', 'Direct Delivery', 'Uengage', 'Ownly']);
const DINEIN_CHANNELS = new Set(['Dine In', 'Dine-in']);
const PICKUP_CHANNELS = new Set(['Pick Up', 'Pickup', 'Pick-up']);

function classify(channel: string): 'delivery' | 'dinein' | 'pickup' | 'other' {
  if (DELIVERY_CHANNELS.has(channel)) return 'delivery';
  if (DINEIN_CHANNELS.has(channel)) return 'dinein';
  if (PICKUP_CHANNELS.has(channel)) return 'pickup';
  // Fallback: guess from name
  const lower = channel.toLowerCase();
  if (/deliver|swiggy|zomato|dunzo|ownly/i.test(lower)) return 'delivery';
  if (/dine|pos.*eat|table/i.test(lower)) return 'dinein';
  if (/pick|takeaway/i.test(lower)) return 'pickup';
  return 'other';
}

export function useChannelSplit({
  outletId,
  period,
}: {
  outletId: string | 'all';
  period: PeriodKey;
}) {
  const mix = useChannelMix({ outletId, period });

  const split = useMemo(() => {
    const rows = mix.data?.rows ?? [];
    return computeSplit(rows);
  }, [mix.data?.rows]);

  return {
    ...split,
    isLoading: mix.isLoading,
    isError: mix.isError,
    totalSales: mix.data?.totalSales ?? 0,
  };
}

export function computeSplit(rows: ChannelRow[]): {
  delivery: SplitBucket;
  dineIn: SplitBucket;
  pickUp: SplitBucket;
  total: { sales: number; orders: number };
} {
  const buckets = {
    delivery: { sales: 0, orders: 0, prevSales: 0, channels: [] as string[] },
    dinein: { sales: 0, orders: 0, prevSales: 0, channels: [] as string[] },
    pickup: { sales: 0, orders: 0, prevSales: 0, channels: [] as string[] },
  };

  for (const r of rows) {
    const kind = classify(r.channel);
    if (kind === 'other') continue;
    const key = kind === 'dinein' ? 'dinein' : kind === 'pickup' ? 'pickup' : 'delivery';
    buckets[key].sales += r.sales;
    buckets[key].orders += r.orders;
    // metric.previous is prior-period sales for this channel
    const prev = r.metric?.previous ?? 0;
    buckets[key].prevSales += prev;
    if (r.sales > 0 && !buckets[key].channels.includes(r.channel)) {
      buckets[key].channels.push(r.channel);
    }
  }

  const totalSales = buckets.delivery.sales + buckets.dinein.sales + buckets.pickup.sales;
  const totalOrders = buckets.delivery.orders + buckets.dinein.orders + buckets.pickup.orders;

  function toBucket(
    label: SplitBucket['label'],
    b: { sales: number; orders: number; prevSales: number; channels: string[] },
  ): SplitBucket {
    return {
      label,
      sales: b.sales,
      orders: b.orders,
      sharePct: totalSales > 0 ? (b.sales / totalSales) * 100 : 0,
      deltaPct: b.prevSales > 0 ? ((b.sales - b.prevSales) / b.prevSales) * 100 : null,
      channels: b.channels,
    };
  }

  return {
    delivery: toBucket('Delivery', buckets.delivery),
    dineIn: toBucket('Dine-in', buckets.dinein),
    pickUp: toBucket('Pick-up', buckets.pickup),
    total: { sales: totalSales, orders: totalOrders },
  };
}
